import { Branch } from '@/client/branches';
import { File } from '@/client/files';
import { Group } from '@/client/groups';
import { Reseller } from '@/client/resellers';
import { MailSystemType, SendingProfileType } from '@/client/sender-profiles';
import { User } from '@/client/users';
import { TableNamesEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { UserManagementMeta } from './account-user-management.type';
import {
  AccountCourseDelivery,
  AccountManagerReportFrequency,
} from './accounts.type';

export type AzureADConfig = {
  groupId?: string;
  clientId?: string;
  clientSecret?: string;
  tenantId?: string;
  syncEnabled?: boolean;
  hasAd?: boolean;
};

export type HubspotConfig = {
  hubspotId?: string;
  hubspotDomains?: string[];
  lifeCycleStage?: string;
  awtStatus?: string;
  phishingStatus?: string;
  securityCultureStatus?: string;
  autoPhishingStatus?: string;
  phishingOneOffStatus?: string;
  whiteGloveSecurityCulture?: string;
  companyOwner?: string;
  awtOnboardingDate?: string;
  phishingOnboardingDate?: string;
  autoPhishingOnboardingDate?: string;
  securityCultureOnboardingDate?: string;
  phishingOneOffOnboardingDate?: string;
};

export type AccountSSOConfig = {
  idpEntityId?: string;
  singleSignOnServiceUrl?: string;
  signingCertificate?: string;
  displayName?: string;
  importUrl?: string;
  isSSO?: boolean;
  isManual?: boolean;
};

export type AccountGoogleSSOConfig = {
  displayName?: string;
  clientId?: string;
  clientSecret?: string;
  isSSO?: boolean;
};

export type AccountAPIConfig = {
  active: boolean;
  username: string;
  password: string;
  clientId: string;
  clientSecret: string;
  authenticationUrl: string;
};

export type AccountKeycloak = {
  realmName: string;
  saml: {
    identityProviderUrl: string;
    certificateFingerprint: string;
    remoteSigninUrl: string;
    remoteSignoutUrl: string;
    targetedIdentifier: string;
    firstNameIdentifier: string;
    lastNameIdentifier: string;
    emailIdentifier: string;
  };
};

type AccountMeta = {
  goPhishId: string;
  goPhishApiKey: string;
  goPhishPassword: string;
  goPhishURL: string;
  keycloak?: AccountKeycloak;
  isMigrationTriggered?: boolean;
  isSSO?: boolean;
  reportsLastGeneratedAt?: Date;
  notifyManagerForOverdueCourses?: boolean;
};

export type AccountHubspotCompanyOwner = {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
};

export enum AccountHubspotProductsOnActiveOrders {
  AWT = 'AWT',
  PHISHING_SUBSCRIPTION = 'Phishing Subscription',
  AUTO_PHISHING = 'Auto Phishing',
  SECURITY_CULTURE = 'Security Culture',
}

export enum LifecycleStage {
  SUBSCRIBER = 'subscriber',
  LEAD = 'lead',
  MARKETING_QUALIFIED_LEAD = 'marketingqualifiedlead',
  SALES_QUALIFIED_LEAD = 'salesqualifiedlead',
  OPPORTUNITY = 'opportunity',
  CUSTOMER = 'customer',
  EVANGELIST = 'evangelist',
  CHURNED = '51277555',
  PARTNER_QUALIFIED_LEAD = '428007387',
  OTHER = 'other',
}

export const lifecycleStageDisplayLabel: { [key: string]: string } = {
  [LifecycleStage.SUBSCRIBER]: 'Subscriber',
  [LifecycleStage.LEAD]: 'Lead',
  [LifecycleStage.MARKETING_QUALIFIED_LEAD]: 'Marketing Qualified Lead',
  [LifecycleStage.SALES_QUALIFIED_LEAD]: 'Sales Qualified Lead',
  [LifecycleStage.OPPORTUNITY]: 'Opportunity',
  [LifecycleStage.CUSTOMER]: 'Customer',
  [LifecycleStage.EVANGELIST]: 'Evangelist',
  [LifecycleStage.CHURNED]: 'Churned',
  [LifecycleStage.PARTNER_QUALIFIED_LEAD]: 'Partner Qualified Lead',
  [LifecycleStage.OTHER]: 'Other',
};

export type AccountHubspot = {
  id?: string;
  linkingFailed?: boolean;
  lastSyncedAt?: Date;
  lifeCycleStage?: LifecycleStage;
  awtStatus?: string;
  phishingStatus?: string;
  securityCultureStatus?: string;
  autoPhishingStatus?: string;
  phishingOneOffStatus?: string;
  whiteGloveSecurityCulture?: string;
  companyOwner?: AccountHubspotCompanyOwner;
  primaryDomain?: string;
  additionalDomains?: string[];
  url?: string;
  productsOnActiveOrders: AccountHubspotProductsOnActiveOrders[];
  awtOnboardingDate?: string | null;
  phishingOnboardingDate?: string | null;
  autoPhishingOnboardingDate?: string | null;
  securityCultureOnboardingDate?: string | null;
  phishingOneOffOnboardingDate?: string | null;
};

export type AccountSMTPSettings = {
  senderName?: string;
  fromAddress?: string;
  host?: string;
  username?: string;
  password?: string;
  ignoreCertErrors?: boolean;
  isCustom?: boolean;
  isRateLimited?: boolean | null;
};

export type AccountSettings = {
  smtp?: AccountSMTPSettings;
  timezone?: string | null;
  phishingFollowUpEnabled?: boolean;
  phishingFollowUpEmailEnabled?: boolean;
};

export enum AccountTypeEnum {
  REAL = 'real',
  DEMO = 'demo',
}

export type PersonalizedTableViews = {
  key: TableNamesEnum;
  columns: string[];
};

export enum AccountDataRemovalMethod {
  DELETE = 'delete',
  ANONYMIZE = 'anonymize',
}

export const dataRemovalConfirmWord = 'DELETE';

export type Account = {
  id: string;
  name: string;
  subdomain: string;
  active: boolean;
  activeCampaignsCount: number;
  created: Date;
  updated: Date;
  isSystem: boolean;
  syncStatus?: SyncStatusEnum;
  ssoSettings: AccountSSOConfig;
  users: User[];
  groups: Group[];
  branches: Branch[];
  settings: AccountSettings;
  meta: AccountMeta;
  blockSystemEmails: boolean;
  isFreeTrial: boolean;
  freeTrialEndsAt?: Date;
  freeTrialDeletionDate?: Date;
  freeTrialFullAccess: boolean;
  awarenessGoLiveDate?: Date | null;
  reseller: Reseller | null;
  platformSettings: PlatformSettings | null;
  availableCoursesByType: Record<string, number> | null;
  mainAccountOfReseller: Reseller | null;
  defaultLanguage: string | null;
  type: AccountTypeEnum | null;
  hubspot?: AccountHubspot;
  activeDirectoryIntegration?: UserManagementMeta[] | null;
  activeUsersCount: number;
  totalUsersCount: number;
  courseDelivery?: AccountCourseDelivery;
  courseDueDays?: number;
  featureFlagAbility?: {
    action: string;
    subject: string | string[];
  }[];
  courseMinimumScore?: number | null;
  personalizedTableViews?: PersonalizedTableViews[];
  courseReTrainGracePeriodDays?: number | null;
  companyLogo?: File | null;
  guidelinesFile?: File | null;
  branding?: AccountBranding;
  customerSuccessManager?: AccountCustomerSuccessManager;
  enableVulnerableUsers?: boolean;
  mailSystem?: MailSystemType | null;
  sendingProfileType?: SendingProfileType;
  enableUserflow?: boolean;
  companyDescription?: string | null;
  customCompanyDescription?: string | null;
  isPhishingSenderWhitelisted?: boolean;
  allowPosthogTracking?: boolean;
  managerReport?: boolean;
  managerReportFrequency?: AccountManagerReportFrequency;
  gracePeriodDays?: number;
  dataRemovalMethod?: AccountDataRemovalMethod;
  enableAutoDeleteInactiveUsers?: boolean;
  autoDeleteInactiveUsersDays?: number;
  linkedInDescription?: string;
  enableAutoPhishingMode?: boolean | null;
  enableAiTrainingForVulnerableUsers?: boolean | null;
  osintEnabled?: boolean;
  osintScrapingStatus?: 'idle' | 'in-progress' | 'complete' | 'failed';
  dpaAmendmentAcceptedAt?: Date | null;
  dpaAmendmentAcceptedByUserId?: string | null;
  dpaAmendmentAcceptedByName?: string | null;
  scAutoScheduleEnabled?: boolean;
};

export type AccountBranding = {
  itSupportSectionTitle?: string;
  itSupportName?: string;
  itSupportTitle?: string;
  itSupportEmail?: string;
  itSupportPhone?: string;
};

export type AccountCustomerSuccessManager = {
  email?: string;
  name?: string;
  jobTitle?: string;
  phone?: string;
  avatar?: string;
};

export type PlatformSettings = {
  logo: string;
  name: string;
  url: string;
  baseUrl: string;
  theme: string;
  favIcon?: string;
  phone?: string;
  email?: string;
  subdomain?: string;
};

export enum AccountActionsEnum {
  CREATE = 'create',
  UPDATE = 'update',
}

export const displayAccountSyncStatus = (
  status: SyncStatusEnum,
  t: TranslationFunctionType,
): string => t(`sync.status.${status}`);

export enum SyncStatusEnum {
  SYNCED = 'synced',
  PENDING = 'pending',
  NOT_SYNCED = 'not-synced',
}

export const SUPPORT_EMAIL = 'support@cyberpilot.io';
export const SUPPORT_PHONE = '+45 32 67 26 26';
export const INFO_EMAIL = 'info@cyberpilot.io';
